/**
 * The transcript, interleaved with each turn's directive, verdict summary,
 * status events and submits — the one screen that answers "what did the user
 * say, what did the reasoner decide, what did the interviewer do" (plan §4,
 * PD-2).
 *
 * The row model is `trace.ts`; this file only draws it. The split matters: the
 * ORDER of these rows is the claim the screen makes about what happened, and it
 * is asserted directly in `trace.test.ts` rather than through the DOM.
 */
import { useMemo, useState } from 'react';
import type { ConversationTrace } from '../../api/types';
import { buildRows, plural, summarizeRows } from './trace';
import TurnRow from './TurnRow';
import PassRow from './PassRow';
import EventRow from './EventRow';
import GapMarker from './GapMarker';

export default function ConversationTimeline({
  trace,
  expandAll = false,
}: {
  trace: ConversationTrace;
  expandAll?: boolean;
}) {
  const rows = useMemo(() => buildRows(trace), [trace]);
  const counts = useMemo(() => summarizeRows(rows), [rows]);
  const [showEvents, setShowEvents] = useState(true);

  const visible = showEvents ? rows : rows.filter((r) => r.kind !== 'event');

  if (rows.length === 0) {
    return (
      <div className="card text-sm text-gray-500" data-testid="timeline-empty">
        No turns, reconcile passes or match events for this pebble.
        {trace.exists ? ' The pebble exists but its conversation has not started.' : ''}
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="timeline">
      <div className="flex items-center gap-3 flex-wrap text-xs text-gray-600">
        <span>
          {plural(counts.turns, 'turn')} ({counts.userTurns} user · {counts.aiTurns} ai
          {counts.systemTurns > 0 ? ` · ${counts.systemTurns} system` : ''})
        </span>
        <span>{plural(counts.passes, 'reconcile pass', 'reconcile passes')}</span>
        {counts.failedPasses > 0 && <span className="text-red-700">{counts.failedPasses} failed</span>}
        {counts.supersededPasses > 0 && <span className="text-amber-700">{counts.supersededPasses} superseded</span>}
        <span>{plural(counts.submits, 'submit decision')}</span>
        <span>{plural(counts.events, 'match event')}</span>
        {/* Possible duplicates are counted in the header, from the payload's own
            `timeline_meta`, and marked on each flagged turn. Repeating the total
            here would put the same number on the screen twice with two different
            provenances — and the one an operator would then trust is whichever
            they read second. */}
        <label className="ml-auto flex items-center gap-1 cursor-pointer">
          <input type="checkbox" checked={showEvents} onChange={(e) => setShowEvents(e.target.checked)} />
          match events
        </label>
      </div>

      <div className="space-y-3">
        {visible.map((row) => {
          if (row.kind === 'turn') return <TurnRow key={row.key} row={row} defaultOpen={expandAll} />;
          if (row.kind === 'pass') return <PassRow key={row.key} row={row} />;
          if (row.kind === 'event') return <EventRow key={row.key} row={row} />;
          return <GapMarker key={row.key} row={row} />;
        })}
      </div>
    </div>
  );
}
